/* eslint-disable react-refresh/only-export-components -- route module exports the router object + helpers, not just components */
import { lazy, Suspense, type ReactNode } from 'react';
import { createBrowserRouter, isRouteErrorResponse, useRouteError } from 'react-router-dom';
import { KioskShell } from './kiosk-shell';
import { id } from '@/i18n/id';
import { kioskBasePath } from '@/lib/config/kiosk-config';

// Route-level code splitting — each screen is its own bundle so the service
// worker can pre-cache per route and the initial JS stays under budget.
const WelcomeScreen = lazy(() => import('@/screens/welcome/welcome-screen'));
const MenuScreen = lazy(() => import('@/screens/menu/menu-screen'));
const CartScreen = lazy(() => import('@/screens/cart/cart-screen'));
const ConfirmationScreen = lazy(() => import('@/screens/confirmation/confirmation-screen'));
const InstructionsScreen = lazy(() => import('@/screens/instructions/instructions-screen'));

function Fallback() {
  return (
    <div className="flex h-full items-center justify-center text-ink-muted">{id.common.loading}</div>
  );
}

function lazyScreen(node: ReactNode) {
  return <Suspense fallback={<Fallback />}>{node}</Suspense>;
}

/**
 * Detail line for whoever is debugging the deployment, not the customer —
 * mirrors `KioskConfigError`. Most routing errors here trace back to a
 * `basename` mismatch (page served from a directory the router doesn't know
 * about), so the raw status/message is worth surfacing.
 */
function routeErrorDetail(error: unknown): string {
  if (isRouteErrorResponse(error)) return `${error.status} ${error.statusText}`;
  if (error instanceof Error) return error.message;
  return String(error);
}

function ErrorScreen() {
  const error = useRouteError();
  return (
    <div className="flex h-full flex-col items-center justify-center gap-4 px-10 text-center">
      <h1 className="text-3xl font-bold">{id.common.errorTitle}</h1>
      <p className="text-lg text-ink-muted">{id.common.errorBody}</p>
      <code className="mt-4 max-w-full break-words text-xs text-ink-faint">
        {routeErrorDetail(error)}
      </code>
    </div>
  );
}

const routes = [
  {
    path: '/',
    element: <KioskShell />,
    errorElement: <ErrorScreen />,
    children: [
      { index: true, element: lazyScreen(<WelcomeScreen />) },
      { path: 'menu', element: lazyScreen(<MenuScreen />) },
      { path: 'cart', element: lazyScreen(<CartScreen />) },
      { path: 'confirm', element: lazyScreen(<ConfirmationScreen />) },
      { path: 'instructions/:orderId', element: lazyScreen(<InstructionsScreen />) },
    ],
  },
];

/**
 * Basename defaults to this device's own directory (`/kiosk-2`, `''` at the
 * server root) so one shared build resolves routes correctly no matter which
 * kiosk directory serves it. Overridable for tests.
 */
export function createRouter(basename: string = kioskBasePath()) {
  return createBrowserRouter(routes, { basename });
}
